import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Truck, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const COURIERS = ["JNE", "SPX", "JNT", "HALUU"];
const rupiah = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

export default function OngkirPanel() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/ongkir").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { try { await api.delete(`/ongkir/${id}`); load(); } catch (e) { toast.error("Gagal menghapus"); } };
  const total = rows.reduce((s, r) => s + (Number(r.nominal) || 0), 0);

  return (
    <div data-testid="ongkir-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-showty-navy flex items-center gap-2"><Truck className="w-5 h-5 text-showty-red" /> Data Ongkir KOL</h3>
          <p className="text-sm text-muted-foreground">Alamat kirim, kurir (JNE/SPX/JNT/HALUU), dan nominal produk per KOL.</p>
        </div>
        <OngkirDialog onDone={load} trigger={<Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="ongkir-add-btn"><Plus className="w-4 h-4 mr-1" /> Tambah Data</Button>} />
      </div>

      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-showty-navy text-white">
              <tr className="text-left">
                <th className="px-3 py-2 text-xs font-medium">No</th>
                <th className="px-3 py-2 text-xs font-medium">Nama KOL</th>
                <th className="px-3 py-2 text-xs font-medium">Alamat</th>
                <th className="px-3 py-2 text-xs font-medium">Kurir</th>
                <th className="px-3 py-2 text-xs font-medium">Nominal Produk</th>
                <th className="px-3 py-2 text-xs font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground" data-testid="ongkir-empty">Belum ada data ongkir</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.id} className="border-t border-border even:bg-showty-cream/40" data-testid={`ongkir-${r.id}`}>
                  <td className="px-3 py-2">{i + 1}</td>
                  <td className="px-3 py-2 font-medium text-showty-navy">{r.name}</td>
                  <td className="px-3 py-2 max-w-[280px] text-muted-foreground">{r.address || "—"}</td>
                  <td className="px-3 py-2"><span className="rounded-full bg-showty-pink/40 text-showty-navy px-2 py-0.5 text-xs font-medium">{r.courier || "—"}</span></td>
                  <td className="px-3 py-2 font-semibold text-showty-red">{rupiah(r.nominal)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <OngkirDialog row={r} onDone={load} trigger={<button className="text-muted-foreground hover:text-showty-navy" data-testid={`ongkir-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>} />
                      <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`ongkir-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-showty-cream">
                  <td colSpan={4} className="px-3 py-2 text-right font-medium text-showty-navy">Total Nominal</td>
                  <td className="px-3 py-2 font-display text-showty-red" data-testid="ongkir-total">{rupiah(total)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

const OngkirDialog = ({ row, onDone, trigger }) => {
  const [open, setOpen] = useState(false);
  const empty = { name: "", address: "", courier: "JNE", nominal: "", note: "" };
  const [f, setF] = useState(row || empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setF(row || empty); }, [open]);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Nama KOL wajib diisi");
    const payload = { name: f.name, address: f.address, courier: f.courier, nominal: Number(f.nominal) || 0, note: f.note };
    try {
      if (row) await api.patch(`/ongkir/${row.id}`, payload);
      else await api.post("/ongkir", payload);
      toast.success(row ? "Data diperbarui" : "Data ditambahkan");
      setOpen(false); onDone?.();
    } catch (e) { toast.error("Gagal menyimpan"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ongkir-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">{row ? "Edit Data Ongkir" : "Tambah Data Ongkir"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Nama KOL</Label><Input className="mt-1" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="ongkir-name" /></div>
          <div><Label>Alamat Pengiriman</Label><Input className="mt-1" value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="ongkir-address" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kurir</Label>
              <Select value={f.courier} onValueChange={(v) => set("courier", v)}>
                <SelectTrigger className="mt-1" data-testid="ongkir-courier"><SelectValue /></SelectTrigger>
                <SelectContent>{COURIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nominal Produk (Rp)</Label><Input type="number" className="mt-1" value={f.nominal} onChange={(e) => set("nominal", e.target.value)} data-testid="ongkir-nominal" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="ongkir-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
